import {
  Component, OnInit, ViewEncapsulation,
  ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges, SimpleChanges,
} from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';

import { User } from '@app/modules/core/interfaces/user';
import { CountriesVm } from '@booking/entities/countries';
import { BillingCity, BillingResponse, BillingVm, BillingCreateItem } from '@billing/entities/billing.payload';
import { BILLING_FORM_INPUTS, BOOKING_FORM_INPUTS, CONTROL } from '@booking/constants/forms.constants';
import { BillingFormService } from '@billing/services/billing-form.service';
import { SearchDataDefaultUbication } from '@search-bar/entities/search-data-default';
import { UserAddresses } from '@app/modules/core/interfaces/address';
import { TravelerPaxResponse } from '@pax/entities/traveler-pax';
import { BusinessRulesByBookingType } from '@app/entities/booking-documents';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { AssetBookingConfig } from '@asset-booking-config';

@Component({
  selector: 'app-billing-form',
  templateUrl: './billing-form.component.html',
  styleUrls: ['./billing-form.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BillingFormComponent implements OnInit, OnChanges {

  @Input() formRef: UntypedFormGroup;
  @Input() countries: CountriesVm[] = [];
  @Input() cities: BillingCity[] = [];
  @Input() user: User;
  @Input() firstPaxSelection: TravelerPaxResponse;
  @Input() businessRules: BusinessRulesByBookingType;
  @Input() bookingType: TravelBookingType;
  @Input() withErrorLaunch: boolean;
  @Input() showSwipeUpForm: boolean;
  @Input() defaultCityByIp: SearchDataDefaultUbication;
  @Input() defaultBilling: BillingResponse;
  @Input() userAddresses: UserAddresses;
  @Input() isMobile: boolean;

  @Output() toggleShowForm: EventEmitter<void> = new EventEmitter();
  @Output() closeForm: EventEmitter<void> = new EventEmitter();
  @Output() update: EventEmitter<BillingVm> = new EventEmitter();
  @Output() create: EventEmitter<BillingCreateItem> = new EventEmitter();

  public billingForm: UntypedFormGroup;
  public activeNaturalPerson: boolean = false;

  constructor(
    private billingFormService: BillingFormService
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.addDynamicElement.addControl(CONTROL, this.billingForm);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.defaultBilling) this.start();
  }

  get hiddenSwitch(): boolean {
    return !!this.defaultBilling && !!this.defaultBilling._id;
  } 

  get hasUserInfo(): boolean {
    return this.billingFormService.hasUserInfo(this.user);
  }

  get hasDefaultBillingInfo(): boolean {
    return this.billingFormService.hasDefaultBillingInfo(this.defaultBilling);
  }

  get isNaturalPerson(): boolean {
    return (this.hasDefaultBillingInfo && this.defaultBilling.billing_info.natural_person) ||
      (!this.hasDefaultBillingInfo);
  }

  get addDynamicElement(): UntypedFormGroup {
    return this.formRef.get(BOOKING_FORM_INPUTS.BILLING) as UntypedFormGroup;
  }

  get descriptionMessage(): string {
    return !this.hiddenSwitch ? 'BOOKING_MODULE.BILLING.BILLING_SUBTITLE' :
      this.isNaturalPerson ? 'BOOKING_MODULE.BILLING.BILLING_SUBTITLE_NATURAL' :
        'BOOKING_MODULE.BILLING.BILLING_SUBTITLE_JURIDICAL';
  }

  get disabledCompanyBillingForm(): boolean {
    return AssetBookingConfig.BILLING_MODULE.DISABLED_BILLING_COMPANY_FORM;
  }

  get showCompanyBillingForm(): boolean {
    return !this.disabledCompanyBillingForm && !this.activeNaturalPerson;
  }

  public onClickSwitch(type: boolean): void {
    this.activeNaturalPerson = type;
  }

  public start(): void {
    this.createForm();
    this.activeNaturalPerson = this.isNaturalPerson;
  }

  private createForm(): void {
    this.billingForm = new UntypedFormGroup({
      [BILLING_FORM_INPUTS.USER_ID]: new UntypedFormControl(
        this.hasDefaultBillingInfo ? this.defaultBilling.user_id : this.hasUserInfo ? this.user.id : null,
        Validators.required
      )
    });
  }

  public updateForm(event: BillingVm): void {
    this.update.emit(event);
  }

  public closeBillingForm(): void {
    this.closeForm.emit();
  }

}
